import { saveAs } from 'file-saver';
import * as XLSX from 'xlsx';
import { formatPKR } from '../api';

export const exportOrdersCSV = (orders, filename = 'orders.csv') => {
  const rows = orders.map((o) => ({
    orderId: o.orderId,
    date: o.date,
    time: o.time,
    customer: o.customerName,
    email: o.customerEmail,
    phone: o.customerPhone,
    status: o.status,
    total: o.orderTotal,
    products: o.items.map((i) => `${i.productName} x${i.quantity}`).join('; '),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Orders');
  const csv = XLSX.utils.sheet_to_csv(ws);
  saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), filename);
};

export const exportOrdersExcel = (orders, filename = 'orders.xlsx') => {
  const rows = orders.map((o) => ({
    'Order ID': o.orderId,
    Date: o.date,
    Time: o.time,
    Customer: o.customerName,
    Email: o.customerEmail,
    Phone: o.customerPhone,
    Status: o.status,
    Total: o.orderTotal,
    Products: o.items.map((i) => `${i.productName} x${i.quantity}`).join('; '),
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Orders');
  XLSX.writeFile(wb, filename);
};

export const exportOrdersReportExcel = (orders, { periodType, from, to, category = 'Orders' }) => {
  const periodLabel = periodType === 'weekly' ? 'Weekly' : 'Monthly';
  const slug = category.toLowerCase().replace(/\s+/g, '-');
  const filename = `lumora-${slug}-${periodType}-${from}-to-${to}.xlsx`;

  const rows = orders.map((o) => ({
    'Order ID': o.orderId,
    Date: o.date,
    Time: o.time,
    Customer: o.customerName,
    Email: o.customerEmail,
    Phone: o.customerPhone,
    Status: o.status,
    Total: o.orderTotal,
    Payment: o.paymentMethod,
    Address: `${o.address || ''}, ${o.city || ''}`.trim(),
    Products: o.items.map((i) => `${i.productName} x${i.quantity}`).join('; '),
  }));

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(rows);
  XLSX.utils.book_append_sheet(wb, ws, `${periodLabel} Orders`);

  if (periodType === 'monthly') {
    const revenue = orders.reduce((s, o) => s + o.orderTotal, 0);
    const productsSold = orders.reduce(
      (s, o) => s + o.items.reduce((si, i) => si + i.quantity, 0),
      0
    );
    const summary = XLSX.utils.aoa_to_sheet([
      ['LUMORA Order Report Summary'],
      ['Category', category],
      ['Period', `${from} to ${to}`],
      ['Report Type', periodLabel],
      ['Total Orders', orders.length],
      ['Total Revenue', revenue],
      ['Products Sold', productsSold],
    ]);
    XLSX.utils.book_append_sheet(wb, summary, 'Summary');
  }

  XLSX.writeFile(wb, filename);
};

export const exportDispatchedReportExcel = (orders, { periodType, from, to }) => {
  const periodLabel = periodType === 'weekly' ? 'Weekly' : 'Monthly';
  const filename = `lumora-dispatched-${periodType}-${from}-to-${to}.xlsx`;

  const rows = orders.map((o) => ({
    'Order ID': o.orderId,
    Date: o.date,
    Customer: o.customerName,
    Products: o.items.map((i) => `${i.productName} x${i.quantity}`).join('; '),
    Quantity: o.items.reduce((s, i) => s + i.quantity, 0),
    Total: o.orderTotal,
    Payment: o.paymentMethod,
    Address: `${o.address || ''}, ${o.city || ''}`.trim(),
    'Dispatch Date': o.date,
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), `${periodLabel} Dispatched`);

  if (periodType === 'monthly') {
    const revenue = orders.reduce((s, o) => s + o.orderTotal, 0);
    const productsSold = orders.reduce(
      (s, o) => s + o.items.reduce((si, i) => si + i.quantity, 0),
      0
    );
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([
        ['LUMORA Dispatched Orders Summary'],
        ['Period', `${from} to ${to}`],
        ['Total Orders', orders.length],
        ['Total Revenue', revenue],
        ['Products Sold', productsSold],
      ]),
      'Summary'
    );
  }

  XLSX.writeFile(wb, filename);
};

export const exportReturnedReportExcel = (orders, returns, { periodType, from, to }) => {
  const periodLabel = periodType === 'weekly' ? 'Weekly' : 'Monthly';
  const filename = `lumora-returned-${periodType}-${from}-to-${to}.xlsx`;
  const returnMap = {};
  returns.forEach((r) => {
    returnMap[r.orderId] = r;
  });

  const rows = orders.map((o) => {
    const ret = returnMap[o.orderId] || {};
    return {
      'Order ID': o.orderId,
      'Order Date': o.date,
      Customer: o.customerName,
      'Return Date': ret.date || o.date,
      Reason: ret.notes || '—',
      'Refund Status': 'Processed',
      Products: o.items.map((i) => `${i.productName} x${i.quantity}`).join('; '),
      Total: o.orderTotal,
    };
  });

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), `${periodLabel} Returned`);
  XLSX.writeFile(wb, filename);
};

export const exportOwnerWithdrawalsReportExcel = (finance, { periodType, from, to }) => {
  const periodLabel = periodType === 'weekly' ? 'Weekly' : 'Monthly';
  const ownerSlug = finance.owner.name.toLowerCase().replace(/\s+/g, '-');
  const filename = `lumora-${ownerSlug}-withdrawals-${periodType}-${from}-to-${to}.xlsx`;

  const rows = finance.withdrawals.map((w) => ({
    ID: w.id,
    Date: w.date,
    Amount: w.amount,
    'Payment Method': w.paymentMethod,
    Notes: w.notes,
    'Approved By': w.approvedBy,
    Status: w.status,
  }));

  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), `${periodLabel} Withdrawals`);

  if (periodType === 'monthly') {
    const total = finance.withdrawals.reduce((s, w) => s + w.amount, 0);
    XLSX.utils.book_append_sheet(
      wb,
      XLSX.utils.aoa_to_sheet([
        ['LUMORA Owner Withdrawals Summary'],
        ['Owner', finance.owner.name],
        ['Period', `${from} to ${to}`],
        ['Total Withdrawals', finance.withdrawals.length],
        ['Total Amount', total],
        ['Total Earnings', finance.totalEarnings],
        ['Available Balance', finance.availableBalance],
      ]),
      'Summary'
    );
  }

  XLSX.writeFile(wb, filename);
};

export const exportWithdrawalsCSV = (withdrawals, ownerName) => {
  const rows = withdrawals.map((w) => ({
    id: w.id,
    amount: w.amount,
    date: w.date,
    paymentMethod: w.paymentMethod,
    notes: w.notes,
    approvedBy: w.approvedBy,
    status: w.status,
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const csv = XLSX.utils.sheet_to_csv(ws);
  saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `${ownerName}-withdrawals.csv`);
};

export const exportWithdrawalsExcel = (withdrawals, ownerName) => {
  const rows = withdrawals.map((w) => ({
    ID: w.id,
    Amount: w.amount,
    Date: w.date,
    'Payment Method': w.paymentMethod,
    Notes: w.notes,
    'Approved By': w.approvedBy,
    Status: w.status,
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Withdrawals');
  XLSX.writeFile(wb, `${ownerName}-withdrawals.xlsx`);
};

export const printOrders = (orders, title) => {
  const html = `
    <html><head><title>${title}</title>
    <style>body{font-family:Inter,sans-serif;padding:24px}table{width:100%;border-collapse:collapse}
    th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#EEE6D8}</style></head>
    <body><h1>LUMORA — ${title}</h1>
    <table><thead><tr><th>ID</th><th>Date</th><th>Customer</th><th>Status</th><th>Total</th></tr></thead>
    <tbody>${orders
      .map(
        (o) =>
          `<tr><td>#${o.orderId}</td><td>${o.date}</td><td>${o.customerName}</td><td>${o.status}</td><td>${formatPKR(o.orderTotal)}</td></tr>`
      )
      .join('')}</tbody></table></body></html>`;
  const w = window.open('', '_blank');
  w.document.write(html);
  w.document.close();
  w.print();
};
